import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppShell } from "../components/layout/AppShell";
import { NeuCard, NeuSelect } from "../components/neu/Neu";
import { useAuth } from "../context/AuthContext";
import { useCachedResource } from "../hooks/useCachedResource";
import { fetchKpiMonthly } from "../api/kpi";
import { listBranches } from "../api/directory";
import { KpiTable } from "../features/kpi/KpiTable";
import { KpiDetail } from "../features/kpi/KpiDetail";
import { KpiWeights } from "../features/kpi/KpiWeights";
import { CountUp } from "../components/neu/CountUp";

import { ADMIN_ROLES } from "../utils/role";

const months = () => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};

export default function KpiPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [month, setMonth] = useState(months()[0]);
  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(null);

  const { data, reload: load } = useCachedResource(
    `kpi:${month}:${branch}`,
    () => fetchKpiMonthly({ month, ...(branch ? { branch_id: branch } : {}) }),
    [month, branch]
  );

  useEffect(() => {
    listBranches().then(setBranches).catch(() => setBranches([]));
  }, []);

  return (
    <AppShell title={t("kpi.title")} subtitle={t("kpi.subtitle")}>
      <div className="grid gap-5" data-testid="kpi-page">
        <NeuCard className="grid gap-3 p-4 sm:grid-cols-3" data-testid="kpi-filters">
          <label className="grid gap-1 text-xs font-bold">
            {t("kpi.month")}
            <NeuSelect data-testid="kpi-month" value={month} onChange={(e) => setMonth(e.target.value)}>
              {months().map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </NeuSelect>
          </label>
          <label className="grid gap-1 text-xs font-bold">
            {t("penalty.filters.branch")}
            <NeuSelect data-testid="kpi-branch" value={branch} onChange={(e) => setBranch(e.target.value)}>
              <option value="">{t("penalty.filters.all")}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </NeuSelect>
          </label>
          <div className="flex flex-wrap items-end gap-2 text-xs">
            {data ? (
              <>
                <span className="rounded-full bg-primary/10 px-3 py-1 font-bold text-primary">
                  {t("kpi.average")}: <CountUp value={Number(data.totals.average)} />
                </span>
                <span className="rounded-full bg-destructive/10 px-3 py-1 font-bold text-destructive">
                  {t("kpi.penalised")}: <CountUp value={Number(data.totals.penalised)} />
                </span>
              </>
            ) : null}
          </div>
        </NeuCard>

        <KpiWeights canEdit={ADMIN_ROLES.includes(user?.role)} onSaved={load} />
        {open ? <KpiDetail row={open} onClose={() => setOpen(null)} /> : null}
        <KpiTable rows={data?.rows || []} onOpen={setOpen} />
      </div>
    </AppShell>
  );
}
